// The arranger: dress a melody + chord-per-bar in a standard accompaniment
// texture, elaborated to a difficulty `level`, → a DrillNote stream. Pure leaf
// (no engine/render/RNG). The melody passes through as the right hand; the
// texture renders the left hand over each bar's chord. Never alters the melody
// or chord roots — a tune stays recognizably itself up the ladder. Tags are
// exact: TECHNIQUE_SKILLS fixes the emitted piece's `uses`.

import type { SkillId } from "../types";
import { type ChordQuality, chordOffsets, type DrillNote, noteH } from "./note-math";

/** A chord rooted at a concrete bass MIDI pitch (already in the LH register). */
export interface DrillChord {
  root: number;
  quality: ChordQuality;
}

export interface ArrangeInput {
  /** RH melody, hand "R", beats absolute across the whole tune. */
  melody: DrillNote[];
  /** One chord per bar. */
  chords: DrillChord[];
  /** Beats per bar (4 = common time, 3 = waltz/carol). */
  barBeats: number;
}

type Technique =
  | "melody"
  | "pedal"
  | "drone"
  | "block"
  | "broken"
  | "alberti"
  | "arpeggio"
  | "waltz";

/** Skills each technique applies — the texture half of an emitted piece's `uses`. The
 *  triad-*shape* skill (major/minor) is derived per-tune from the chords it plays —
 *  see `chordShapeSkills` — so a minor tune tags `triad-minor`, not `triad-major`. */
export const TECHNIQUE_SKILLS: Record<Technique, SkillId[]> = {
  melody: ["five-finger"],
  // A single sustained tonic ROOT under the melody — one finger, no chord. The gentlest
  // accompaniment, tagged five-finger only, so it fills the gap before any chord is taught.
  pedal: ["five-finger"],
  // A held tonic triad = "block, but only the I chord" — the gentlest blocked-chord
  // study and the one-chord rung between the five-finger drill and the 2-chord tunes.
  drone: ["blocked-chords"],
  block: ["blocked-chords"],
  broken: ["broken-chords"],
  alberti: ["alberti-bass"],
  arpeggio: ["arpeggios", "broken-chords"],
  waltz: ["blocked-chords"],
};

/** Techniques that strike the triad as a block — so the triad-shape skill is *applied*. */
export const BLOCKED_TECHNIQUES: ReadonlySet<Technique> = new Set(["drone", "block", "waltz"]);

const SHAPE_BY_QUALITY: Record<ChordQuality, SkillId> = {
  maj: "triad-major",
  min: "triad-minor",
  dom7: "dom7",
};

/** The triad-shape skills a blocked texture exercises over `chords` (deduped) — the
 *  `uses` shape half. A minor chord → `triad-minor`; a dom7 → `dom7`. */
export function chordShapeSkills(chords: readonly DrillChord[]): SkillId[] {
  return [...new Set(chords.map((c) => SHAPE_BY_QUALITY[c.quality]))];
}

const TONIC_PC: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };

/** Parse a `"<tonic> major|minor"` key string into its tonic pitch-class + mode, or
 *  null when it doesn't match (so an unknown key tags nothing). */
function parseKey(key: string): { tonic: number; mode: "major" | "minor" } | null {
  const m = /^([a-g])([#b♯♭]?)\s+(major|minor)$/i.exec(key.trim());
  const base = m ? TONIC_PC[(m[1] ?? "").toLowerCase()] : undefined;
  if (!m || base === undefined) return null;
  const accidental = /[#♯]/.test(m[2] ?? "") ? 1 : /[b♭]/.test(m[2] ?? "") ? -1 : 0;
  const mode = (m[3] ?? "").toLowerCase() === "minor" ? "minor" : "major";
  return { tonic: (((base + accidental) % 12) + 12) % 12, mode };
}

// A chord slot: the degree (semitones above the major-key tonic) + the qualities that
// count there. A progression is recognized either as an ordered **sequence** (its chords
// in consecutive bars, cyclic — the tune literally plays the loop) or, where the defining
// trait is a chord *vocabulary* rather than an order, as a **present** set.
interface Slot {
  degree: number;
  quality: ChordQuality[];
}
type Progression = { id: SkillId; sequence: Slot[] } | { id: SkillId; present: Slot[] };

const PROGRESSIONS: Progression[] = [
  // The pop loop: I → V(/V7) → vi → IV, consecutive.
  {
    id: "prog-I-V-vi-IV",
    sequence: [
      { degree: 0, quality: ["maj"] },
      { degree: 7, quality: ["maj", "dom7"] },
      { degree: 9, quality: ["min"] },
      { degree: 5, quality: ["maj"] },
    ],
  },
  // The jazz cadence: ii(min) → V7 → I, consecutive (so an incidental Dm–G7–F doesn't count).
  {
    id: "prog-ii-V-I",
    sequence: [
      { degree: 2, quality: ["min"] },
      { degree: 7, quality: ["maj", "dom7"] },
      { degree: 0, quality: ["maj"] },
    ],
  },
  // The blues: dominant-7ths on I, IV and V — a dom7 tonic is the giveaway (it appears
  // nowhere else in the corpus), so the vocabulary alone identifies it.
  {
    id: "prog-12-bar-blues",
    present: [
      { degree: 0, quality: ["dom7"] },
      { degree: 5, quality: ["dom7"] },
      { degree: 7, quality: ["dom7"] },
    ],
  },
];

const slotMatches = (c: Slot | undefined, want: Slot): boolean =>
  c !== undefined &&
  c.degree === want.degree &&
  want.quality.includes(c.quality[0] as ChordQuality);

/** The progression skills a chord-per-bar set *applies* in its key (deduped). Detected
 *  only in major keys — these loops are major-key vocabulary; minor tags nothing. */
export function progressionSkills(chords: readonly DrillChord[], key: string): SkillId[] {
  const parsed = parseKey(key);
  if (parsed?.mode !== "major") return [];
  const tonic = parsed.tonic;
  const bars: Slot[] = chords.map((c) => ({
    degree: (((Math.round(c.root) - tonic) % 12) + 12) % 12,
    quality: [c.quality],
  }));
  const matches = (p: Progression): boolean => {
    if ("present" in p) return p.present.every((want) => bars.some((b) => slotMatches(b, want)));
    const n = bars.length;
    const L = p.sequence.length;
    if (n < L) return false;
    return bars.some((_, i) => p.sequence.every((want, k) => slotMatches(bars[(i + k) % n], want)));
  };
  return PROGRESSIONS.filter(matches).map((p) => p.id);
}

type BarRenderer = (
  chord: DrillChord,
  start: number,
  barBeats: number,
  level: number,
) => DrillNote[];

function melodyBar(chord: DrillChord, start: number, barBeats: number): DrillNote[] {
  return [noteH(chord.root, start, barBeats, "L")];
}

/** Hold the (tonic) triad for the whole bar — a sustained one-chord drone. `arrange`
 *  feeds the tonic for every bar, so the piece uses exactly one chord. */
function droneBar(chord: DrillChord, start: number, barBeats: number): DrillNote[] {
  return chordOffsets(chord.quality).map((o) => noteH(chord.root + o, start, barBeats, "L"));
}

function blockBar(chord: DrillChord, start: number, barBeats: number, level: number): DrillNote[] {
  const tones = chordOffsets(chord.quality).map((o) => chord.root + o);
  const beats = level >= 2 ? Array.from({ length: barBeats }, (_, i) => i) : [0];
  const dur = level >= 2 ? 1 : barBeats;
  return beats.flatMap((b) => tones.map((m) => noteH(m, start + b, dur, "L")));
}

function brokenBar(chord: DrillChord, start: number, barBeats: number): DrillNote[] {
  const tones = chordOffsets(chord.quality).map((o) => chord.root + o);
  // `?? chord.root` is unreachable (the modulo index is always in range) — it only
  // satisfies noUncheckedIndexedAccess.
  return Array.from({ length: barBeats }, (_, b) =>
    noteH(tones[b % tones.length] ?? chord.root, start + b, 1, "L"),
  );
}

function albertiBar(chord: DrillChord, start: number, barBeats: number): DrillNote[] {
  if (barBeats !== 4) throw new Error("alberti requires a 4/4 bar");
  const tones = chordOffsets("maj").map((o) => chord.root + o);
  // `?? chord.root` is unreachable (maj offsets always yield three tones) —
  // it only satisfies noUncheckedIndexedAccess.
  const third = chord.quality === "min" ? chord.root + 3 : (tones[1] ?? chord.root);
  const fifth = tones[2] ?? chord.root;
  const cell = [chord.root, fifth, third, fifth];
  return Array.from({ length: 8 }, (_, i) =>
    noteH(cell[i % 4] ?? chord.root, start + i * 0.5, 0.5, "L"),
  );
}

function arpeggioBar(chord: DrillChord, start: number, barBeats: number): DrillNote[] {
  if (barBeats !== 4) throw new Error("arpeggio requires a 4/4 bar");
  const t = chordOffsets(chord.quality)
    .slice(0, 3)
    .map((o) => chord.root + o);
  // `?? chord.root` is unreachable (every quality yields ≥3 tones) — it only
  // satisfies noUncheckedIndexedAccess.
  const root = t[0] ?? chord.root;
  const third = t[1] ?? chord.root;
  const fifth = t[2] ?? chord.root;
  const oct = chord.root + 12;
  const shape = [root, third, fifth, oct, oct, fifth, third, root];
  return shape.map((m, i) => noteH(m, start + i * 0.5, 0.5, "L"));
}

function waltzBar(chord: DrillChord, start: number, barBeats: number): DrillNote[] {
  if (barBeats !== 3) throw new Error("waltz requires a 3/4 bar");
  const tones = chordOffsets(chord.quality).map((o) => chord.root + o);
  // `?? chord.root` is unreachable (every quality yields ≥3 tones) — it only
  // satisfies noUncheckedIndexedAccess.
  const third = tones[1] ?? chord.root;
  const fifth = tones[2] ?? chord.root;
  return [
    noteH(chord.root, start, 1, "L"),
    noteH(third, start + 1, 1, "L"),
    noteH(fifth, start + 1, 1, "L"),
    noteH(third, start + 2, 1, "L"),
    noteH(fifth, start + 2, 1, "L"),
  ];
}

const RENDERERS: Partial<Record<Technique, BarRenderer>> = {
  melody: melodyBar,
  pedal: melodyBar, // single root; `arrange` feeds it the tonic every bar (a sustained pedal)
  drone: droneBar,
  block: blockBar,
  broken: brokenBar,
  alberti: albertiBar,
  arpeggio: arpeggioBar,
  waltz: waltzBar,
};

/** Dress `input` in `technique` at difficulty `level` → RH melody + LH texture. */
export function arrange(input: ArrangeInput, technique: Technique, level: number): DrillNote[] {
  const render = RENDERERS[technique];
  if (!render) throw new Error(`arrange: unsupported technique "${technique}"`);
  // Drone/pedal sustain the tonic (the first bar's chord) under every bar → one chord.
  const tonic = input.chords[0];
  const holdsTonic = technique === "drone" || technique === "pedal";
  const accompaniment = input.chords.flatMap((chord, bar) =>
    render(holdsTonic && tonic ? tonic : chord, bar * input.barBeats, input.barBeats, level),
  );
  return [...input.melody, ...accompaniment];
}
